import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AddModal } from '../../../../../component/modal';
import { colors } from '../../../../../component/colors';
import { DropDownWithLabel } from '../../../../../component/dropDown';
import { Input } from '../../../../../component/input';
import { ImagePicker } from '../../../../../component/imagePicker';
import { useFormik } from 'formik';
import { editPropertyValidation } from '../../../validation';
import { showErrorToast, showSuccessToast } from '../../../../../utils/toast';
import { useEditPropertyMutation } from '../../../../../store/realEstate/api';
import { useFetchAgentQuery } from '../../../../../store/agent/api';
import { TextAreaWithLabel } from '../../../../../component/textArea';
import { IProperty } from '../../../../../store/realEstate/interface';
import { landUnit } from '../../../../../utils/module';
import { generateUrl } from '../../../../../utils/urlGenerator';
import useDebounce from '../../../../../utils/useDebounce';
import { SearchableDropDownWithLabel } from '../../../../../component/dropDown/searchableDropDownWthLabel';
type IEditProperty = {
  show?: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  propertyItems: IProperty | undefined;
};
type FormikValues = {
  category: string;
  price: string;
  type: string;
  noOfRooms: number;
  noOfBath: number;
  roomSize: string;
  agent: {
    name: string;
  };
  description: string;
  title: string;
  inspectionFee: string;
  landSize: number;
  landUnit: string;
  id: string;
  file: File[] | null;
};
export const EditProperty: FC<IEditProperty> = ({ setShow, propertyItems }) => {
  const [searchAgent, setSearchAgent] = useState('');
  const debounceSearchAgent = useDebounce(searchAgent, 500);
  const { data: agents } = useFetchAgentQuery(
    generateUrl({ module: 'real-estate', search: debounceSearchAgent })
  );
  const [editProperty, { isLoading: isEditPropertyLoading }] =
    useEditPropertyMutation();
  const handleFilePicked = (file: File[]) => {
    setFieldValue('file', file);
  };
  const agentsList = useMemo(() => {
    return (
      agents?.data.fetchedData.map((agents) => ({
        name: agents.name,
        imageUrl: agents.file.url
      })) ?? []
    );
  }, [agents]);

  const handleEditProperty = (values: FormikValues) => {
    if (!agents?.data) return;
    const formData = new FormData();
    formData.append('category', values.category.toLowerCase());
    if (values.price !== propertyItems?.price) {
      formData.append('price', values.price);
    }
    formData.append('type', values.type.toLowerCase());
    if (values.type.toLowerCase() === 'land') {
      formData.append('landSize[value]', values.landSize.toString());
      formData.append(
        'landSize[unit]',
        values.landUnit.toString().toLowerCase()
      );
    } else {
      if (values.noOfRooms !== propertyItems?.noOfRooms) {
        formData.append('noOfRooms', values.noOfRooms.toString());
      }
      if (values.roomSize !== propertyItems?.roomSize) {
        formData.append('roomSize', values.roomSize);
      }
      formData.append('noOfBath', values.noOfBath.toString());
    }

    const selectedAgent = agents?.data?.fetchedData.find((item) => {
      return item.name === values.agent.name;
    });
    if (selectedAgent && selectedAgent._id !== propertyItems?.agent._id) {
      formData.append('agent', selectedAgent._id);
    }
    if (values.description !== propertyItems?.description) {
      formData.append('description', values.description);
    }
    if (values.title !== propertyItems?.title) {
      formData.append('title', values.title);
    }
    if (values.inspectionFee !== propertyItems?.inspectionFee) {
      formData.append('inspectionFee', values.inspectionFee);
    }
    if (values.file) {
      values.file.forEach((file) => {
        formData.append('file', file);
      });
    }
    formData.append('id', values.id);
    editProperty(formData)
      .unwrap()
      .then((result) => {
        showSuccessToast(result.message);
        setShow(false);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };
  const { values, touched, handleChange, handleSubmit, setFieldValue, errors } =
    useFormik({
      initialValues: {
        category: propertyItems?.category ?? '',
        price: propertyItems?.price ?? '',
        type: propertyItems?.type ?? '',
        noOfRooms: propertyItems?.noOfRooms ?? 0,
        noOfBath: propertyItems?.noOfBath ?? 0,
        roomSize: propertyItems?.roomSize ?? '',
        agent: { name: propertyItems?.agent?.name ?? '', index: 0 },
        description: propertyItems?.description ?? '',
        title: propertyItems?.title ?? '',
        inspectionFee: propertyItems?.inspectionFee ?? '',
        id: propertyItems?._id ?? '',
        file: null,
        landSize: Number(propertyItems?.landSize.value) ?? 0,
        landUnit: propertyItems?.landSize.unit ?? ''
      },
      validationSchema: editPropertyValidation,
      onSubmit: handleEditProperty
    });
  const propertyType = useMemo(() => {
    let typeOfProperty = ['Apartment', 'Bungalow', 'Duplex', 'Flat', 'Land'];
    if (values.category === 'Rent') {
      typeOfProperty.splice(4);
    }
    return typeOfProperty;
  }, [values.category]);
  return (
    <AddModal
      title="Edit Property"
      disableBtn={isEditPropertyLoading}
      btnTxt={'Update'}
      btnClick={handleSubmit}
      closeModal={() => setShow(false)}
      isLoading={isEditPropertyLoading}
    >
      <Container>
        <Row>
          <CustomDropDown
            label="Category"
            value={values.category}
            onSelect={(item) => {
              setFieldValue('category', item);
              setFieldValue('type', '');
            }}
            options={['Buy', 'Lease', 'Rent']}
          />
          <CustomDropDown
            label="Type"
            value={values.type}
            onSelect={(item) => {
              setFieldValue('type', item);
            }}
            options={propertyType}
          />
        </Row>
        <Row>
          <CustomInput
            labelText="Price"
            value={values.price}
            onChange={handleChange('price')}
            placeholder="Enter property price"
            errorMessage={touched.price ? errors.price : ''}
          />
          <CustomInput
            labelText="Title"
            value={values.title}
            onChange={handleChange('title')}
            placeholder="Enter title"
            errorMessage={touched.title ? errors.title : ''}
          />
        </Row>
        {propertyItems?.type === 'land' ? (
          <Row>
            <CustomInput
              labelText="Land size"
              placeholder="Enter land size"
              type={'number'}
              value={values.landSize}
              onChange={handleChange('landSize')}
              errorMessage={touched.landSize ? errors.landSize : ''}
            />
            <CustomDropDown
              label="Land unit"
              placeholder="Choose land unit"
              value={values.landUnit}
              errorMessage={touched.landUnit ? errors.landUnit : ''}
              onSelect={(item) => {
                setFieldValue('landUnit', item);
              }}
              options={landUnit}
            />
          </Row>
        ) : (
          <Row>
            <CustomInput
              labelText="Number of rooms"
              value={values.noOfRooms}
              type="number"
              min={1}
              onChange={handleChange('noOfRooms')}
              errorMessage={touched.noOfRooms ? errors.noOfRooms : ''}
            />
            <CustomInput
              labelText="Number of baths"
              value={values.noOfBath}
              type="number"
              min={1}
              onChange={handleChange('noOfBath')}
              errorMessage={touched.noOfBath ? errors.noOfBath : ''}
            />
          </Row>
        )}
        <Row>
          <SearchableCustomDropDown
            search={searchAgent}
            setSearch={setSearchAgent}
            label="SOJI agent"
            value={values.agent.name}
            onSelect={(item, index) => {
              setFieldValue('agent', { name: item, index });
            }}
            options={agentsList}
            errorMessage={touched.agent && errors.agent ? '*required' : ''}
          />
          <CustomInput
            labelText="Inspection fee"
            placeholder="Enter Inspection fee"
            errorMessage={touched.inspectionFee ? errors.inspectionFee : ''}
            onChange={handleChange('inspectionFee')}
            value={values.inspectionFee}
          />
        </Row>
        <Row>
          {propertyItems?.type !== 'land' && (
            <CustomInput
              labelText="Room size (sqrm)"
              placeholder="Enter room size"
              value={values.roomSize}
              onChange={handleChange('roomSize')}
              errorMessage={touched.roomSize ? errors.roomSize : ''}
            />
          )}
        </Row>
        <CustomTextarea
          labelText="Description"
          errorMessage={touched.description ? errors.description : ''}
          placeholder="Enter description"
          value={values.description}
          onChange={handleChange('description')}
        />
        <CustomImagePicker
          initialValue={propertyItems?.file.map((image) => image.url)}
          onFilePicked={handleFilePicked}
        />
      </Container>
    </AddModal>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0px 40px;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`;
const CustomInput = styled(Input)`
  flex: 0.46;
  color: ${colors.textDark};
  margin-bottom: 16px;
`;
const CustomImagePicker = styled(ImagePicker)`
  margin-bottom: 32px;
`;

const CustomDropDown = styled(DropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomTextarea = styled(TextAreaWithLabel)`
  margin-bottom: 16px;
`;
const SearchableCustomDropDown = styled(SearchableDropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
